import React from 'react';
import s from './s.module.less';
import dayjs, { Dayjs } from 'dayjs';
import { Calendar, CalendarProps, Popover } from 'antd';
import moment from 'moment';
import { getDayKey } from 'utils/common';

moment.updateLocale('en', {
    weekdaysMin: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
});

type Prop = {
    wrapClass?: string;
    startDate?: Dayjs;
    endDate?: Dayjs;
    defaultValue?: Dayjs;
    onChange?: (value: Dayjs) => void;
    availableDates?: string[];
};

export default function DatePicker(props: Prop) {
    const { wrapClass, startDate, endDate, onChange, availableDates, defaultValue } = props;
    const [currentVal, setCurrentVal] = React.useState<Dayjs>(defaultValue || dayjs());
    const [isOpen, setIsOpen] = React.useState(false);

    const onPanelChange = (value: Dayjs, mode: CalendarProps<Dayjs>['mode']) => {
        setCurrentVal(value);
    };

    const addMonth = (month: number) => {
        onChange && onChange(currentVal.add(month, 'month'));
        setCurrentVal((prev) => prev.add(month, 'month'));
    };
    const firstDayOfMonth = currentVal.startOf('month');
    const lastDayOfMonth = currentVal.endOf('month');

    const canBack = startDate ? firstDayOfMonth.unix() > startDate?.unix() : true;
    const canNext = endDate ? lastDayOfMonth.unix() < endDate?.unix() : true;

    React.useEffect(() => {
        if (defaultValue) {
            //console.log('defaultValue', defaultValue);
            setCurrentVal(defaultValue);
        }
    }, [defaultValue]);
    //console.log('currentVal', currentVal);

    const headerRender = () => {
        return (
            <div className={s.header}>
                <div
                    onClick={() => {
                        if (!canBack) {
                            return;
                        }
                        addMonth(-1);
                    }}
                    className={`${s.left} ${canBack ? s.leftCan : ''}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className={s.center}>
                    {currentVal?.format('MMMM YYYY')}
                </div>
                <div
                    onClick={() => {
                        if (!canNext) {
                            return;
                        }
                        addMonth(1);
                    }}
                    className={`${s.right} ${canNext ? s.rightCan : ''}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <div
            className={s.wrap}
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <Popover
                trigger="click"
                onOpenChange={(open) => {
                    setIsOpen(open);
                }}
                open={isOpen}
                content={
                    <div className={`${s.cWrap} ${isOpen ? s.cWrapOpen : ''}`}>
                        <Calendar
                            value={currentVal}
                            headerRender={headerRender}
                            fullscreen={false}
                            //onPanelChange={onPanelChange}
                            onChange={(value) => {
                                setIsOpen(false);
                                setCurrentVal(value);
                                onChange && onChange(value);
                            }
                            }
                            disabledDate={(current) => {
                                if (availableDates) {
                                    if (!availableDates.includes(getDayKey(current))) {
                                        return true;
                                    }
                                }
                                if (startDate && endDate) {
                                    if (getDayKey(current) === getDayKey(startDate) || getDayKey(current) === getDayKey(endDate)) {
                                        return false;
                                    }
                                    return current <= startDate || current >= endDate;
                                }
                                return false;
                            }}
                        />
                    </div>
                }
            >
                <div className={s.labelWrap}>
                    <div className={s.label}>
                        <span>{currentVal?.format('MMMM YYYY')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                            <path d="M3.33268 10.5L7.99935 5.83333L12.666 10.5" stroke="#8E8E8E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            </Popover>
        </div>
    );
}
